import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Music, Heart, Send, ExternalLink, Search, Play, X, Check } from "lucide-react";
import { isMediaUrl, embedFor } from "../lib/media";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getDeviceId = () => {
  try {
    let id = localStorage.getItem("hh_device_id");
    if (!id) {
      id = "d_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("hh_device_id", id);
    }
    return id;
  } catch {
    return "anon";
  }
};

export default function Wishes() {
  const [enabled, setEnabled] = useState(false);
  const [wishes, setWishes] = useState([]);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [url, setUrl] = useState("");
  const [guestName, setGuestName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [urlBusy, setUrlBusy] = useState(false);

  // Live search state
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [previewId, setPreviewId] = useState(null); // video_id currently previewing
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimer = useRef(null);
  const searchSeq = useRef(0);
  const suggestionsRef = useRef(null);

  const deviceId = getDeviceId();

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wishes`, { params: { device_id: deviceId } });
      setEnabled(!!res.data?.enabled);
      setWishes(res.data?.wishes || []);
    } catch {
      /* silent */
    }
  }, [deviceId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  // Debounced live search on title/artist change (only if URL is empty)
  useEffect(() => {
    if (url.trim()) {
      // User provided a URL — search doesn't apply, hide suggestions
      setSearchResults([]);
      return;
    }
    const q = [title, artist].filter(Boolean).join(" ").trim();
    if (q.length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearching(true);
    const seq = ++searchSeq.current;
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/wishes/search`, {
          params: { q, limit: 5 },
        });
        if (seq !== searchSeq.current) return; // outdated response
        setSearchResults(res.data?.results || []);
        setShowSuggestions(true);
      } catch {
        if (seq !== searchSeq.current) return;
        setSearchResults([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 500);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [title, artist, url]);

  // Close suggestions on click outside
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (!enabled) return null;

  // Autofill title/artist when guest pastes a URL and title is still empty
  const lookupPastedUrl = async (pastedUrl) => {
    if (!pastedUrl || !isMediaUrl(pastedUrl)) return;
    if (title.trim()) return;
    setUrlBusy(true);
    try {
      const res = await axios.post(`${API}/wishes/lookup`, { url: pastedUrl });
      const t = res.data?.tracks?.[0];
      if (t) {
        setTitle(t.title || "");
        setArtist(t.artist || "");
        if (t.youtube_url) setUrl(t.youtube_url);
        toast.success("Song erkannt");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "YouTube-Link nicht erkannt");
    } finally {
      setUrlBusy(false);
    }
  };

  const pickSuggestion = (r) => {
    setTitle(r.title);
    setArtist(r.artist);
    setUrl(r.youtube_url);
    setShowSuggestions(false);
    setPreviewId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Bitte einen Songtitel eingeben");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/wishes`, {
        title,
        artist,
        youtube_url: url,
        guest_name: guestName,
        device_id: deviceId,
      });
      toast.success("Danke! Dein Wunsch ist auf der Liste 🎵");
      setTitle("");
      setArtist("");
      setUrl("");
      setSearchResults([]);
      setPreviewId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVote = async (id) => {
    setWishes((prev) =>
      prev.map((w) =>
        w.id === id
          ? {
              ...w,
              voted_by_me: !w.voted_by_me,
              votes: w.voted_by_me ? Math.max(0, w.votes - 1) : w.votes + 1,
            }
          : w
      )
    );
    try {
      await axios.post(`${API}/wishes/${id}/vote`, { device_id: deviceId });
    } catch {
      toast.error("Vote fehlgeschlagen");
      load();
    }
  };

  return (
    <section
      id="wuensche"
      data-testid="section-wishes"
      className="py-24 md:py-32 bg-sage-100/60"
    >
      <div className="container mx-auto px-6 md:px-12 max-w-4xl">
        <div className="text-center mb-14">
          <p className="text-sage-500 uppercase tracking-[0.35em] text-xs mb-6">
            · Musikwünsche ·
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sage-800">
            Dein{" "}
            <span className="font-script text-sage-500 text-5xl sm:text-6xl lg:text-7xl">
              Lieblingssong
            </span>
          </h2>
          <p className="mt-6 text-sage-600 max-w-xl mx-auto">
            Wünsch dir einen Song für die Party — der DJ sieht deine Wünsche
            und die beliebtesten kommen zuerst dran. Vote für die Songs, die
            auf jeden Fall gespielt werden sollen.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="wish-form"
          className="bg-white border border-sage-200 rounded-2xl p-6 md:p-8 shadow-sm mb-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block md:col-span-2 relative" ref={suggestionsRef}>
              <span className="text-xs uppercase tracking-[0.2em] text-sage-600 flex items-center gap-1.5">
                Songtitel *
                {searching && (
                  <span
                    data-testid="wish-searching"
                    className="inline-flex items-center gap-1 text-sage-400 normal-case"
                  >
                    <span className="inline-block w-3 h-3 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin" />
                    sucht auf YouTube...
                  </span>
                )}
              </span>
              <div className="relative">
                <input
                  data-testid="wish-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowSuggestions(true)}
                  required
                  maxLength={200}
                  placeholder="z.B. Perfect  (Ergebnisse erscheinen unter dem Feld)"
                  className="mt-2 w-full rounded-lg border border-sage-300 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 mt-1 -translate-y-1/2 text-sage-400 pointer-events-none"
                />
              </div>

              {/* Live YouTube search suggestions */}
              {showSuggestions && searchResults.length > 0 && !url.trim() && (
                <div
                  data-testid="wish-suggestions"
                  className="absolute z-30 left-0 right-0 mt-1 bg-white border border-sage-200 rounded-xl shadow-lg overflow-hidden max-h-[420px] overflow-y-auto"
                >
                  <div className="flex items-center justify-between px-3 py-2 border-b border-sage-100 bg-sage-50/60">
                    <span className="text-[10px] uppercase tracking-widest text-sage-500">
                      YouTube-Treffer · anhören &amp; auswählen
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className="text-sage-400 hover:text-sage-700"
                      aria-label="Schließen"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <ul>
                    {searchResults.map((r) => {
                      const isPreview = previewId === r.video_id;
                      return (
                        <li
                          key={r.video_id}
                          data-testid={`wish-suggestion-${r.video_id}`}
                          className="border-b border-sage-100 last:border-0"
                        >
                          <div className="flex items-start gap-3 px-3 py-2">
                            <img
                              src={`https://i.ytimg.com/vi/${r.video_id}/mqdefault.jpg`}
                              alt=""
                              className="w-20 h-14 object-cover rounded-md shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sage-800 text-sm truncate">
                                {r.title}
                              </div>
                              <div className="text-xs text-sage-500 truncate">
                                {r.artist}
                              </div>
                              <div className="mt-1.5 flex items-center gap-2">
                                <button
                                  type="button"
                                  data-testid={`wish-preview-${r.video_id}`}
                                  onClick={() =>
                                    setPreviewId(isPreview ? null : r.video_id)
                                  }
                                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wide ${
                                    isPreview
                                      ? "bg-sage-600 text-white"
                                      : "bg-sage-100 text-sage-700 hover:bg-sage-200"
                                  }`}
                                >
                                  {isPreview ? <X size={11} /> : <Play size={11} />}
                                  {isPreview ? "Schließen" : "Anhören"}
                                </button>
                                <button
                                  type="button"
                                  data-testid={`wish-pick-${r.video_id}`}
                                  onClick={() => pickSuggestion(r)}
                                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wide bg-sage-500 hover:bg-sage-600 text-white"
                                >
                                  <Check size={11} /> Wählen
                                </button>
                              </div>
                            </div>
                          </div>
                          {isPreview && (
                            <div className="px-3 pb-3">
                              <div className="rounded-lg overflow-hidden bg-black aspect-video">
                                <iframe
                                  title={`Preview ${r.title}`}
                                  src={`https://www.youtube-nocookie.com/embed/${r.video_id}?autoplay=1&rel=0&modestbranding=1`}
                                  className="w-full h-full"
                                  frameBorder="0"
                                  allow="autoplay; encrypted-media"
                                  allowFullScreen
                                />
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-sage-600">
                Künstler
              </span>
              <input
                data-testid="wish-artist"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                maxLength={200}
                placeholder="z.B. Ed Sheeran"
                className="mt-2 w-full rounded-lg border border-sage-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-sage-600">
                Dein Name
              </span>
              <input
                data-testid="wish-guest"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                maxLength={120}
                placeholder="damit der DJ weiß, von wem"
                className="mt-2 w-full rounded-lg border border-sage-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-[0.2em] text-sage-600 flex items-center gap-2">
                YouTube-Link{" "}
                <span className="normal-case text-sage-400">
                  (optional — Titel &amp; Künstler werden automatisch geladen)
                </span>
                {urlBusy && (
                  <span
                    data-testid="wish-url-busy"
                    className="inline-flex items-center gap-1 text-sage-400 normal-case"
                  >
                    <span className="inline-block w-3 h-3 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin" />
                    prüft...
                  </span>
                )}
              </span>
              <input
                data-testid="wish-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onPaste={(e) => {
                  const p = e.clipboardData?.getData("text");
                  if (p) setTimeout(() => lookupPastedUrl(p), 100);
                }}
                onBlur={(e) => lookupPastedUrl(e.target.value)}
                type="url"
                maxLength={500}
                placeholder="https://www.youtube.com/... oder https://open.spotify.com/..."
                className="mt-2 w-full rounded-lg border border-sage-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
              />
              {url && (() => {
                const e = embedFor(url);
                if (!e) return null;
                const sp = e.provider === "spotify";
                return (
                  <div
                    data-testid="wish-url-preview"
                    className={`mt-3 rounded-lg overflow-hidden max-w-md ${
                      sp ? "" : "bg-black aspect-video"
                    }`}
                  >
                    <iframe
                      title={`${e.provider} Preview`}
                      src={e.src}
                      className="w-full"
                      style={e.height ? { height: e.height } : { height: "100%" }}
                      height={e.height || undefined}
                      frameBorder="0"
                      allow="encrypted-media; clipboard-write"
                      allowFullScreen
                    />
                  </div>
                );
              })()}
            </label>
          </div>
          <div className="mt-6 flex justify-center">
            <button
              type="submit"
              data-testid="wish-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-8 py-3 text-sm uppercase tracking-wider shadow"
            >
              <Send size={14} />
              {submitting ? "Wird gesendet..." : "Wunsch senden"}
            </button>
          </div>
        </form>

        {wishes.length > 0 && (
          <div>
            <h3 className="font-serif text-2xl text-sage-800 flex items-center gap-2 mb-4">
              <Music size={18} /> Aktuelle Wünsche
            </h3>
            <ul className="space-y-2">
              {wishes.map((w, i) => (
                <li
                  key={w.id}
                  data-testid={`wish-item-${w.id}`}
                  className={`bg-white border rounded-xl px-4 py-3 flex items-center gap-3 ${
                    w.played
                      ? "border-sage-200 opacity-60"
                      : "border-sage-200 hover:border-sage-300"
                  }`}
                >
                  <div className="w-8 text-center font-serif text-sage-400 text-sm shrink-0">
                    {i + 1}.
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`font-medium text-sage-800 truncate ${
                          w.played ? "line-through" : ""
                        }`}
                      >
                        {w.title}
                      </span>
                      {w.artist && (
                        <span className="text-sm text-sage-500 truncate">
                          · {w.artist}
                        </span>
                      )}
                      {w.played && (
                        <span className="text-[10px] uppercase tracking-widest rounded-full bg-sage-100 text-sage-500 px-2 py-0.5">
                          gespielt
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-sage-500 mt-0.5">
                      {w.guest_name && <span>von {w.guest_name}</span>}
                      {w.youtube_url && (
                        <a
                          href={w.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 hover:text-sage-700"
                        >
                          <ExternalLink size={10} /> YouTube
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`wish-vote-${w.id}`}
                    onClick={() => toggleVote(w.id)}
                    disabled={w.played}
                    className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                      w.voted_by_me
                        ? "bg-sage-500 text-white hover:bg-sage-600"
                        : "bg-sage-100 text-sage-700 hover:bg-sage-200"
                    } ${w.played ? "opacity-50 cursor-not-allowed" : ""}`}
                    aria-label={w.voted_by_me ? "Vote zurückziehen" : "Voten"}
                  >
                    <Heart
                      size={12}
                      fill={w.voted_by_me ? "currentColor" : "none"}
                    />
                    {w.votes}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
